/**
 * Pins external store.
 *
 * A tiny framework-agnostic store consumed via `useSyncExternalStore` (see
 * `use-pins.ts`). Living outside React lets every surface — cards on a list
 * page AND the SecondRail in the shell — share one source of truth and stay
 * in sync without prop-drilling or a context provider in the tree.
 *
 * Persistence: localStorage, namespaced per user (`armoriq:pins:v1:<userKey>`)
 * so pins are personal and "follow the account". Cross-tab edits sync via the
 * `storage` event. Swap the localStorage calls for an API in one place when
 * the backend lands — the consumer API doesn't change.
 */
import type { PinInput, PinnedItem } from './types';

type Listener = () => void;

const KEY_PREFIX = 'armoriq:pins:v1:';

let userKey = 'anon';
let items: ReadonlyArray<PinnedItem> = [];
const listeners = new Set<Listener>();

function storageKey(uk: string): string {
  return `${KEY_PREFIX}${uk}`;
}

function read(uk: string): PinnedItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(uk));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PinnedItem[]) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userKey), JSON.stringify(items));
  } catch {
    // best-effort
  }
}

function emit(): void {
  listeners.forEach((l) => l());
}

// Cross-tab sync: reload + emit when our key changes in another tab.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key !== storageKey(userKey)) return;
    items = read(userKey);
    emit();
  });
}

/** Point the store at a user. No-op if unchanged. Called by the hook. */
export function setPinUser(uk: string): void {
  if (uk === userKey) return;
  userKey = uk;
  items = read(uk);
  emit();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Stable reference between mutations — required by useSyncExternalStore. */
export function getSnapshot(): ReadonlyArray<PinnedItem> {
  return items;
}

function has(kind: PinnedItem['kind'], id: string): boolean {
  return items.some((p) => p.kind === kind && p.id === id);
}

export function addPin(input: PinInput): void {
  if (has(input.kind, input.id)) return;
  items = [{ ...input, pinnedAt: Date.now() }, ...items];
  persist();
  emit();
}

export function removePin(kind: PinnedItem['kind'], id: string): void {
  if (!has(kind, id)) return;
  items = items.filter((p) => !(p.kind === kind && p.id === id));
  persist();
  emit();
}

export function togglePin(input: PinInput): void {
  if (has(input.kind, input.id)) removePin(input.kind, input.id);
  else addPin(input);
}
